"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CircleCheck as CheckCircle2, Calendar, ArrowRight, Mail, Phone, MapPin, Loader as Loader2, UserCheck, Sparkles } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const benefits = [{
  title: "100% Direct Consultant Access",
  desc: "You work exclusively with me, Yassine Essaadi. No junior account managers, middle layers, or generic agency templates."
}, {
  title: "Tailored SEO Growth Strategy",
  desc: "Every recommendation is built specifically for your search market, business model, and technical platform."
}, {
  title: "Honest & Practical Execution",
  desc: "Clear deliverables, realistic timeline forecasts, and actionable insights designed for measurable revenue impact."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 rounded-xl bg-card border border-border text-center space-y-4 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-accent/20 text-accent mx-auto flex items-center justify-center">
          <CheckCircle2 className="size-6 text-accent" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">
          Consultation Request Sent
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 cursor-pointer">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Your full name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Yassine Essaadi" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Business email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Business Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@company.com" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourcompany.com" {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Primary SEO Goal or Challenge (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Technical SEO, International expansion, AI Search..." {...field} className="bg-background" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold py-6 shadow-md transition-colors cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Processing Your Request...
          </> : <>
            <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
            Book a Free SEO Consultation
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-3 leading-snug">
        100% confidential. No pushy sales calls. You will speak directly with me.
      </p>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationcta" data-nav="light" className="relative py-20 lg:py-28 bg-background overflow-hidden border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <motion.div className="lg:col-span-7 space-y-8" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider">
              <UserCheck className="size-4 text-primary" />
              <span>Direct Expert Engagement</span>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground leading-tight">
                Book a Free SEO Consultation
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                I limit my active engagements so every business receives my personal, hands-on focus. Tell me about your website, and I will review your search potential before we talk.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-muted/50 border border-border flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-xs">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_cta.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_cta.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_cta.png-wvc-sizes" alt="Yassine Essaadi - SEO Consultant" className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shrink-0" loading="lazy" decoding="async" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground font-serif text-base">Yassine Essaadi</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Independent Consultant</span>
                </div>
                <p className="text-sm text-muted-foreground leading-snug">
                  {"“You work directly with me from day one. I conduct the analysis, formulate the strategy, and oversee every optimization step.”"}
                </p>
              </div>
            </div>

            <div className="space-y-5 pt-2">
              {benefits.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <div className="mt-1 size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <CheckCircle2 className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground font-serif text-base">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-6 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-lg bg-card border border-border flex items-center justify-center shrink-0">
                  <Mail className="size-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Direct Email</div>
                  <a href="mailto:contact@ye-consultancy.com" className="font-medium text-foreground hover:text-primary transition-colors">
                    {"contact@ye-consultancy.com"}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="size-9 rounded-lg bg-card border border-border flex items-center justify-center shrink-0">
                  <Phone className="size-4 text-primary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Direct Line</div>
                  <a href="tel:+447490984430" className="font-medium text-foreground hover:text-primary transition-colors">
                    {"+44 7490984430"}
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="size-4 text-primary shrink-0" />
              <span>{"Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR"}</span>
            </div>

            <div className="pt-2">
              <Button variant="outline" asChild className="cursor-pointer">
                <Link to="/ContactPage">
                  <span>View Full Contact Page</span>
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <Card className="shadow-lg border-border bg-card">
              <CardHeader className="space-y-2 pb-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-accent uppercase tracking-wide">
                  <Sparkles className="size-4" />
                  <span>Free Search Assessment</span>
                </div>
                <CardTitle className="text-2xl font-serif text-foreground">
                  Request Your Strategy Session
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Fill in your details and I will manually review your search footprint before our call.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <FormProvider className="w-full" formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! I will review your website and contact you within 24 hours.">
                  <ConsultationFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}